import React, { useState, useEffect, useCallback } from "react";
import {
  Users,
  UserPlus,
  UserCheck,
  UserX,
  Clock,
  RefreshCw,
  Search,
  Check,
  Building,
  ShieldAlert,
  Loader2,
  X,
  Shield,
  Trash2,
  AlertTriangle,
  Zap,
  Sunrise,
  SunMedium,
  BellOff,
  Bell,
  Pencil,
} from "lucide-react";
import {
  UserProfile,
  UserRole,
  UserStatus,
  NotificationPreference,
} from "../types";
import { NotificationPreferencesModal } from "./NotificationPreferencesModal";
import {
  updateUserGovernance,
  deleteUserRecord,
  fetchUsers,
  triggerHrSync,
  listActiveHrStaff,
  importSelectedHrStaff,
  MinimalHrEmployee,
} from "../services/auth";

export interface UserManagementViewProps {
  currentUser: UserProfile;
  initialUsers?: UserProfile[];
  onFetchUsers?: () => Promise<UserProfile[]>;
  onTriggerHrSync?: () => Promise<{
    syncedCount: number;
    stagedCount?: number;
    updatedCount?: number;
  }>;
  onListActiveHrStaff?: () => Promise<MinimalHrEmployee[]>;
  onImportSelectedHrStaff?: (
    selectedUserIds: string[],
    role?: UserRole,
  ) => Promise<{ importedCount: number; updatedCount: number }>;
  onDeleteUser?: (uid: string) => Promise<void>;
}

const AVAILABLE_APPS = [
  { id: "operations-hub", name: "Operations Hub" },
  { id: "mlc-hr-tools", name: "HR Tools" },
  { id: "viq-autostart", name: "VIQ Autostart" },
  { id: "memoq-bridge", name: "memoQ Bridge" },
  { id: "teppi-portal", name: "Teppi Portal" },
  { id: "sales-pipeline", name: "Sales Pipeline" },
];

function formatRole(role: UserRole): string {
  switch (role) {
    case "it_admin":
      return "IT Admin";
    case "dept_admin":
      return "Dept Admin";
    case "end_user":
    default:
      return "End User";
  }
}

function formatPrefLabel(pref?: NotificationPreference): string {
  switch (pref) {
    case "daily":
      return "Daily (8 AM CT)";
    case "twice_daily":
      return "Twice Daily";
    case "never":
      return "In-App Only";
    case "realtime":
    default:
      return "Real-Time";
  }
}

function getPrefBadgeClass(pref?: NotificationPreference): string {
  switch (pref) {
    case "daily":
      return "bg-sky-50 text-sky-700 border-sky-200";
    case "twice_daily":
      return "bg-teal-50 text-teal-700 border-teal-200";
    case "never":
      return "bg-slate-100 text-slate-600 border-slate-200";
    case "realtime":
    default:
      return "bg-amber-50 text-amber-700 border-amber-200";
  }
}

function renderPrefIcon(pref?: NotificationPreference): React.ReactNode {
  switch (pref) {
    case "daily":
      return <Sunrise className="w-3 h-3 text-sky-500 shrink-0" />;
    case "twice_daily":
      return <SunMedium className="w-3 h-3 text-[#2ba5b5] shrink-0" />;
    case "never":
      return <BellOff className="w-3 h-3 text-slate-400 shrink-0" />;
    case "realtime":
    default:
      return <Zap className="w-3 h-3 text-amber-500 shrink-0" />;
  }
}

export const UserManagementView: React.FC<UserManagementViewProps> = ({
  currentUser,
  initialUsers = [],
  onFetchUsers,
  onTriggerHrSync,
  onListActiveHrStaff,
  onImportSelectedHrStaff,
  onDeleteUser,
}) => {
  const [users, setUsers] = useState<UserProfile[]>(initialUsers);
  const [statusFilter, setStatusFilter] = useState<string>("all");
  const [searchQuery, setSearchQuery] = useState<string>("");
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [isSyncingHr, setIsSyncingHr] = useState<boolean>(false);
  const [syncFeedback, setSyncFeedback] = useState<string | null>(null);
  const [editingUserId, setEditingUserId] = useState<string | null>(null);
  const [isPruning, setIsPruning] = useState<boolean>(false);

  // HR Directory Modal states (Operations App Pattern)
  const [isHrModalOpen, setIsHrModalOpen] = useState<boolean>(false);
  const [hrStaff, setHrStaff] = useState<MinimalHrEmployee[]>([]);
  const [loadingHr, setLoadingHr] = useState<boolean>(false);
  const [hrSearchQuery, setHrSearchQuery] = useState<string>("");
  const [selectedHrStaff, setSelectedHrStaff] =
    useState<MinimalHrEmployee | null>(null);
  const [selectedTier, setSelectedTier] = useState<UserRole>("end_user");
  const [selectedDeptApps, setSelectedDeptApps] = useState<string[]>([]);
  const [isGrantingAccess, setIsGrantingAccess] = useState<boolean>(false);
  const [hrModalError, setHrModalError] = useState<string | null>(null);

  // Approval Modal states for pending users
  const [approvingUser, setApprovingUser] = useState<UserProfile | null>(null);
  const [approvalRole, setApprovalRole] = useState<UserRole>("end_user");
  const [approvalApps, setApprovalApps] = useState<string[]>([]);
  const [isApproving, setIsApproving] = useState<boolean>(false);

  // Form states for inline editing (active / suspended users)
  const [selectedRole, setSelectedRole] = useState<UserRole>("end_user");
  const [selectedApps, setSelectedApps] = useState<string[]>([]);
  const [selectedNotificationPref, setSelectedNotificationPref] =
    useState<NotificationPreference>("realtime");
  const [editingNotificationUser, setEditingNotificationUser] =
    useState<UserProfile | null>(null);

  const effectiveFetchUsers =
    onFetchUsers || (initialUsers.length === 0 ? fetchUsers : undefined);
  const effectiveTriggerHrSync =
    onTriggerHrSync || (() => triggerHrSync(currentUser.email));
  const effectiveListActiveHrStaff =
    onListActiveHrStaff || (() => listActiveHrStaff(currentUser.email));
  const effectiveImportSelectedHrStaff =
    onImportSelectedHrStaff ||
    ((ids: string[], role?: UserRole) =>
      importSelectedHrStaff(ids, role, "active", currentUser.email));
  const effectiveDeleteUser = onDeleteUser || deleteUserRecord;

  const loadUsers = useCallback(async () => {
    if (!effectiveFetchUsers) return;
    setIsLoading(true);
    setErrorMessage(null);
    try {
      const fetched = await effectiveFetchUsers();
      setUsers(fetched);
    } catch (err: unknown) {
      const msg =
        err instanceof Error ? err.message : "Failed to load user directory";
      console.error("Failed to load users:", err);
      setErrorMessage(msg);
    } finally {
      setIsLoading(false);
    }
  }, [effectiveFetchUsers]);

  useEffect(() => {
    if (effectiveFetchUsers) {
      void loadUsers();
    }
  }, [loadUsers, effectiveFetchUsers]);

  // HR Modal Handlers
  const handleOpenHrModal = async () => {
    setIsHrModalOpen(true);
    setLoadingHr(true);
    setHrModalError(null);
    setSelectedHrStaff(null);
    setSelectedTier("end_user");
    setSelectedDeptApps([]);
    setHrSearchQuery("");
    try {
      const staff = await effectiveListActiveHrStaff();
      setHrStaff(staff);
    } catch (err: unknown) {
      const msg =
        err instanceof Error
          ? err.message
          : "Failed to load active staff from HR directory";
      setHrModalError(msg);
    } finally {
      setLoadingHr(false);
    }
  };

  const handleGrantAccessFromHr = async () => {
    if (!selectedHrStaff) return;
    setIsGrantingAccess(true);
    setHrModalError(null);
    try {
      await effectiveImportSelectedHrStaff([selectedHrStaff.id], selectedTier);

      // If dept_admin with specific apps, apply app scopes
      if (selectedTier === "dept_admin" && selectedDeptApps.length > 0) {
        try {
          await updateUserGovernance(selectedHrStaff.id, {
            role: "dept_admin",
            status: "active",
            allowedApps: selectedDeptApps,
            approvedBy: currentUser.uid,
          });
        } catch {
          // If doc ID was prefixed by backend, refresh will synchronize it
        }
      }

      setSyncFeedback(
        `Successfully granted access to ${selectedHrStaff.displayName} as ${formatRole(selectedTier)}.`,
      );
      setIsHrModalOpen(false);
      setSelectedHrStaff(null);

      if (effectiveFetchUsers) {
        await loadUsers();
      } else {
        // Optimistic update
        setUsers((prev) => {
          const exists = prev.some(
            (u) =>
              u.email.toLowerCase() === selectedHrStaff.email.toLowerCase() ||
              u.uid === selectedHrStaff.id,
          );
          if (exists) {
            return prev.map((u) =>
              u.email.toLowerCase() === selectedHrStaff.email.toLowerCase() ||
              u.uid === selectedHrStaff.id
                ? {
                    ...u,
                    role: selectedTier,
                    status: "active",
                    allowedApps:
                      selectedTier === "dept_admin" ? selectedDeptApps : [],
                    approvedBy: currentUser.uid,
                    approvedAt: new Date().toISOString(),
                  }
                : u,
            );
          }
          const newUser: UserProfile = {
            uid: selectedHrStaff.id,
            email: selectedHrStaff.email,
            displayName: selectedHrStaff.displayName,
            department: selectedHrStaff.department,
            role: selectedTier,
            status: "active",
            allowedApps: selectedTier === "dept_admin" ? selectedDeptApps : [],
            approvedBy: currentUser.uid,
            approvedAt: new Date().toISOString(),
            createdAt: new Date().toISOString(),
            updatedAt: new Date().toISOString(),
          };
          return [newUser, ...prev];
        });
      }
    } catch (err: unknown) {
      const msg =
        err instanceof Error ? err.message : "Failed to grant access to staff";
      setHrModalError(msg);
    } finally {
      setIsGrantingAccess(false);
    }
  };

  const toggleDeptAppSelection = (appId: string) => {
    setSelectedDeptApps((prev) =>
      prev.includes(appId)
        ? prev.filter((id) => id !== appId)
        : [...prev, appId],
    );
  };

  const handleQuickSyncAll = async () => {
    if (!effectiveTriggerHrSync) return;
    setIsSyncingHr(true);
    setSyncFeedback(null);
    try {
      const result = await effectiveTriggerHrSync();
      const count = result.syncedCount ?? 0;
      setSyncFeedback(
        `Successfully synced ${count} active employees from HR directory.`,
      );
      if (effectiveFetchUsers) {
        await loadUsers();
      }
    } catch (err: unknown) {
      const message =
        err instanceof Error ? err.message : "HR directory sync failed";
      setSyncFeedback(`Sync failed: ${message}`);
    } finally {
      setIsSyncingHr(false);
    }
  };

  // Approval Modal Handlers (for pending users)
  const handleOpenApprovalModal = (user: UserProfile) => {
    setApprovingUser(user);
    setApprovalRole(user.role || "end_user");
    setApprovalApps(user.allowedApps || []);
  };

  const handleExecuteApproval = async () => {
    if (!approvingUser) return;
    setIsApproving(true);
    try {
      await updateUserGovernance(approvingUser.uid, {
        role: approvalRole,
        status: "active",
        allowedApps: approvalRole === "dept_admin" ? approvalApps : [],
        approvedBy: currentUser.uid,
      });

      const now = new Date().toISOString();
      setUsers((prev) =>
        prev.map((u) =>
          u.uid === approvingUser.uid
            ? {
                ...u,
                role: approvalRole,
                status: "active",
                allowedApps: approvalRole === "dept_admin" ? approvalApps : [],
                approvedBy: currentUser.uid,
                approvedAt: now,
                updatedAt: now,
              }
            : u,
        ),
      );
      setSyncFeedback(
        `Successfully approved ${approvingUser.displayName || approvingUser.email} as ${formatRole(approvalRole)}.`,
      );
      setApprovingUser(null);
    } catch (err: unknown) {
      const msg = err instanceof Error ? err.message : "Failed to approve user";
      console.error("Failed to approve user:", err);
      setErrorMessage(msg);
    } finally {
      setIsApproving(false);
    }
  };

  const toggleApprovalApp = (appId: string) => {
    setApprovalApps((prev) =>
      prev.includes(appId)
        ? prev.filter((id) => id !== appId)
        : [...prev, appId],
    );
  };

  // User deletion / rejection
  const handleDeleteUser = async (userToDelete: UserProfile) => {
    const name = userToDelete.displayName || userToDelete.email;
    if (
      !window.confirm(
        `Are you sure you want to remove ${name} from MLC Ticketing?`,
      )
    ) {
      return;
    }
    try {
      await effectiveDeleteUser(userToDelete.uid);
      setUsers((prev) => prev.filter((u) => u.uid !== userToDelete.uid));
      setSyncFeedback(`Removed ${name} from the system.`);
      if (approvingUser?.uid === userToDelete.uid) {
        setApprovingUser(null);
      }
    } catch (err: unknown) {
      const msg = err instanceof Error ? err.message : "Failed to remove user";
      console.error("Failed to remove user:", err);
      setErrorMessage(msg);
    }
  };

  // Prune unassigned staged pending stubs (preserving any admin or real users like Pablo)
  const stagedPendingUsers = users.filter(
    (u) =>
      u.status === "pending_approval" &&
      u.uid.startsWith("staged_") &&
      u.role === "end_user",
  );

  const handlePruneStagedUsers = async () => {
    if (stagedPendingUsers.length === 0) return;
    if (
      !window.confirm(
        `Are you sure you want to remove ${stagedPendingUsers.length} unapproved staged employee stubs? Real accounts and users with assigned admin roles (such as Pablo) will not be affected.`,
      )
    ) {
      return;
    }

    setIsPruning(true);
    setErrorMessage(null);
    try {
      for (const u of stagedPendingUsers) {
        await effectiveDeleteUser(u.uid);
      }
      setUsers((prev) =>
        prev.filter(
          (u) =>
            !(
              u.status === "pending_approval" &&
              u.uid.startsWith("staged_") &&
              u.role === "end_user"
            ),
        ),
      );
      setSyncFeedback(
        `Successfully pruned ${stagedPendingUsers.length} unapproved staged employee stubs.`,
      );
    } catch (err: unknown) {
      const msg =
        err instanceof Error ? err.message : "Failed to prune staged users";
      setErrorMessage(msg);
    } finally {
      setIsPruning(false);
    }
  };

  // Inline edit handlers for active / suspended users
  const handleOpenEdit = (user: UserProfile) => {
    setEditingUserId(user.uid);
    setSelectedRole(user.role);
    setSelectedApps(user.allowedApps || []);
    setSelectedNotificationPref(user.notificationPreference || "realtime");
  };

  const handleSaveGovernance = async (
    user: UserProfile,
    newStatus?: UserStatus,
  ) => {
    const targetStatus = newStatus || user.status;
    try {
      await updateUserGovernance(user.uid, {
        role: selectedRole,
        status: targetStatus,
        allowedApps: selectedRole === "dept_admin" ? selectedApps : [],
        approvedBy: currentUser.uid,
        notificationPreference: selectedNotificationPref,
      });

      const now = new Date().toISOString();
      setUsers((prev) =>
        prev.map((u) =>
          u.uid === user.uid
            ? {
                ...u,
                role: selectedRole,
                status: targetStatus,
                allowedApps: selectedRole === "dept_admin" ? selectedApps : [],
                approvedBy: currentUser.uid,
                approvedAt:
                  targetStatus === "active" ? now : u.approvedAt || null,
                updatedAt: now,
                notificationPreference: selectedNotificationPref,
              }
            : u,
        ),
      );
      setEditingUserId(null);
    } catch (err: unknown) {
      console.error("Failed to update user governance:", err);
    }
  };

  const toggleAppSelection = (appId: string) => {
    setSelectedApps((prev) =>
      prev.includes(appId)
        ? prev.filter((id) => id !== appId)
        : [...prev, appId],
    );
  };

  const filteredUsers = users.filter((u) => {
    if (statusFilter !== "all" && u.status !== statusFilter) return false;
    if (searchQuery.trim()) {
      const q = searchQuery.toLowerCase();
      const name = (u.displayName || "").toLowerCase();
      const email = (u.email || "").toLowerCase();
      const dept = (u.department || "").toLowerCase();
      if (!name.includes(q) && !email.includes(q) && !dept.includes(q)) {
        return false;
      }
    }
    return true;
  });

  const filteredHrStaff = hrStaff.filter((staff) => {
    if (!hrSearchQuery.trim()) return true;
    const q = hrSearchQuery.toLowerCase();
    const name = (staff.displayName || "").toLowerCase();
    const email = (staff.email || "").toLowerCase();
    const dept = (staff.department || "").toLowerCase();
    const title = (staff.title || "").toLowerCase();
    return (
      name.includes(q) ||
      email.includes(q) ||
      dept.includes(q) ||
      title.includes(q)
    );
  });

  const pendingCount = users.filter(
    (u) => u.status === "pending_approval",
  ).length;

  return (
    <div className="space-y-6">
      {/* Action Header */}
      <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
        <div>
          <h2 className="text-xl font-bold text-slate-900 flex items-center gap-2">
            <Users className="w-5 h-5 text-teal-600" />
            User Access
          </h2>
          <p className="text-xs text-slate-500 mt-0.5">
            Manage organization user roles, department permissions, and
            directory access
          </p>
        </div>

        <div className="flex items-center gap-3">
          <button
            type="button"
            onClick={() => void handleQuickSyncAll()}
            disabled={isSyncingHr}
            className="btn-animate inline-flex items-center gap-2 px-3 py-2 bg-white border border-slate-300 text-slate-700 rounded-lg text-xs font-semibold shadow-xs hover:bg-slate-50 disabled:opacity-50"
            title="Synchronize employee metadata from HR"
          >
            <RefreshCw
              className={`w-3.5 h-3.5 text-teal-600 ${
                isSyncingHr ? "animate-spin" : ""
              }`}
            />
            <span>{isSyncingHr ? "Syncing..." : "Sync from HR"}</span>
          </button>

          <button
            type="button"
            onClick={() => void handleOpenHrModal()}
            className="btn-animate inline-flex items-center gap-2 px-3.5 py-2 bg-teal-600 text-white rounded-lg text-xs font-semibold shadow-sm hover:bg-teal-700 transition-colors"
          >
            <UserPlus className="w-4 h-4" />
            <span>Add from HR Directory</span>
          </button>
        </div>
      </div>

      {/* Sync Feedback Toast */}
      {syncFeedback && (
        <div className="p-3 bg-teal-50 border border-teal-200 text-teal-800 text-xs rounded-lg flex items-center justify-between">
          <div className="flex items-center gap-2">
            <Check className="w-4 h-4 text-teal-600" />
            <span>{syncFeedback}</span>
          </div>
          <button
            type="button"
            onClick={() => setSyncFeedback(null)}
            className="text-teal-600 font-bold hover:text-teal-800"
          >
            ×
          </button>
        </div>
      )}

      {/* Error Alert Banner */}
      {errorMessage && (
        <div className="p-4 bg-rose-50 border border-rose-200 text-rose-800 text-xs rounded-xl flex items-center justify-between">
          <div className="flex items-center gap-2.5">
            <ShieldAlert className="w-4 h-4 text-rose-600 shrink-0" />
            <div>
              <p className="font-semibold">User Governance Notice</p>
              <p className="text-[11px] text-rose-600 mt-0.5">{errorMessage}</p>
            </div>
          </div>
          <button
            type="button"
            onClick={() => void loadUsers()}
            className="px-3 py-1.5 bg-rose-600 text-white rounded-lg text-xs font-semibold hover:bg-rose-700 transition-colors"
          >
            Retry
          </button>
        </div>
      )}

      {/* Helper Banner: Prune Staged Accounts if present */}
      {stagedPendingUsers.length > 0 && currentUser.role === "it_admin" && (
        <div className="p-3.5 bg-amber-50/80 border border-amber-200 rounded-xl text-xs text-amber-900 flex flex-col sm:flex-row sm:items-center justify-between gap-3">
          <div className="flex items-center gap-2.5">
            <AlertTriangle className="w-4 h-4 text-amber-600 shrink-0" />
            <div>
              <span>
                Found{" "}
                <strong>
                  {stagedPendingUsers.length} unapproved staged employee records
                </strong>{" "}
                from a bulk HR sync. You can approve individuals below or prune
                these unapproved stubs.
              </span>
            </div>
          </div>
          <button
            type="button"
            onClick={() => void handlePruneStagedUsers()}
            disabled={isPruning}
            className="px-3 py-1.5 bg-amber-600 text-white rounded-lg text-xs font-semibold hover:bg-amber-700 disabled:opacity-50 transition-colors shrink-0"
          >
            {isPruning
              ? "Pruning..."
              : `Prune Staged Accounts (${stagedPendingUsers.length})`}
          </button>
        </div>
      )}

      {/* Search and Filters Bar */}
      <div className="glass rounded-xl p-4 flex flex-col md:flex-row items-stretch md:items-center justify-between gap-3">
        <div className="flex items-center gap-1 overflow-x-auto pb-1 md:pb-0">
          <button
            type="button"
            onClick={() => setStatusFilter("all")}
            className={`px-3 py-1.5 rounded-lg text-xs font-medium transition-colors ${
              statusFilter === "all"
                ? "bg-teal-600 text-white shadow-xs"
                : "text-slate-600 hover:bg-slate-100"
            }`}
          >
            All ({users.length})
          </button>
          <button
            type="button"
            onClick={() => setStatusFilter("pending_approval")}
            className={`px-3 py-1.5 rounded-lg text-xs font-medium transition-colors inline-flex items-center gap-1.5 ${
              statusFilter === "pending_approval"
                ? "bg-amber-500 text-white shadow-xs"
                : "text-amber-700 hover:bg-amber-50"
            }`}
          >
            <Clock className="w-3.5 h-3.5" />
            Pending Approval
            {pendingCount > 0 && (
              <span className="ml-1 px-1.5 py-0.2 rounded-full bg-white text-amber-800 text-[10px] font-bold">
                {pendingCount}
              </span>
            )}
          </button>
          <button
            type="button"
            onClick={() => setStatusFilter("active")}
            className={`px-3 py-1.5 rounded-lg text-xs font-medium transition-colors ${
              statusFilter === "active"
                ? "bg-teal-600 text-white shadow-xs"
                : "text-slate-600 hover:bg-slate-100"
            }`}
          >
            Active
          </button>
          <button
            type="button"
            onClick={() => setStatusFilter("suspended")}
            className={`px-3 py-1.5 rounded-lg text-xs font-medium transition-colors ${
              statusFilter === "suspended"
                ? "bg-rose-600 text-white shadow-xs"
                : "text-slate-600 hover:bg-slate-100"
            }`}
          >
            Suspended
          </button>
        </div>

        <div className="relative w-full md:w-64">
          <Search className="w-4 h-4 text-slate-400 absolute left-3 top-1/2 -translate-y-1/2" />
          <input
            type="text"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            placeholder="Search users or departments..."
            className="w-full pl-9 pr-3 py-1.5 text-xs bg-white border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-teal-500"
          />
        </div>
      </div>

      {/* Users Table */}
      <div className="glass rounded-xl border border-slate-200 overflow-hidden shadow-xs">
        <div className="overflow-x-auto">
          <table className="w-full text-left text-xs">
            <thead className="bg-slate-100/75 border-b border-slate-200 text-slate-600 font-semibold uppercase tracking-wider">
              <tr>
                <th className="py-3 px-4">User</th>
                <th className="py-3 px-4">Department</th>
                <th className="py-3 px-4">Role</th>
                <th className="py-3 px-4">Status</th>
                <th className="py-3 px-4">App Access</th>
                <th className="py-3 px-4">Notifications</th>
                <th className="py-3 px-4 text-right">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-200">
              {isLoading ? (
                <tr>
                  <td colSpan={7} className="py-8 text-center text-slate-500">
                    <div className="inline-flex items-center gap-2">
                      <div className="w-4 h-4 border-2 border-teal-600 border-t-transparent rounded-full animate-spin" />
                      <span>Loading user directory...</span>
                    </div>
                  </td>
                </tr>
              ) : filteredUsers.length === 0 ? (
                <tr>
                  <td colSpan={7} className="py-8 text-center text-slate-500">
                    No users matching the selected criteria.
                  </td>
                </tr>
              ) : (
                filteredUsers.map((u) => {
                  const isEditing = editingUserId === u.uid;
                  const displayInitial = (u.displayName || u.email || "U")
                    .slice(0, 2)
                    .toUpperCase();

                  return (
                    <tr
                      key={u.uid}
                      className={`hover:bg-slate-50/50 transition-colors ${
                        u.status === "pending_approval" ? "bg-amber-50/30" : ""
                      }`}
                    >
                      <td className="py-3 px-4">
                        <div className="flex items-center gap-3">
                          {u.photoURL ? (
                            <img
                              src={u.photoURL}
                              alt={u.displayName || u.email}
                              className="w-8 h-8 rounded-full object-cover"
                            />
                          ) : (
                            <div className="w-8 h-8 rounded-full bg-teal-600 text-white font-bold flex items-center justify-center text-xs">
                              {displayInitial}
                            </div>
                          )}
                          <div>
                            <p className="font-semibold text-slate-900">
                              {u.displayName || u.email}
                            </p>
                            <p className="text-[11px] text-slate-500">
                              {u.email}
                            </p>
                          </div>
                        </div>
                      </td>
                      <td className="py-3 px-4 text-slate-600">
                        {u.department ? (
                          <span className="inline-flex items-center gap-1">
                            <Building className="w-3 h-3 text-slate-400" />
                            {u.department}
                          </span>
                        ) : (
                          <span className="text-slate-400 italic">
                            Unassigned
                          </span>
                        )}
                      </td>
                      <td className="py-3 px-4">
                        {isEditing ? (
                          <select
                            value={selectedRole}
                            onChange={(e) =>
                              setSelectedRole(e.target.value as UserRole)
                            }
                            className="bg-white border border-slate-300 rounded px-2 py-1 text-xs"
                          >
                            <option value="end_user">End User</option>
                            <option value="dept_admin">Dept Admin</option>
                            <option value="it_admin">IT Admin</option>
                          </select>
                        ) : (
                          <span
                            className={`px-2 py-0.5 rounded-full font-semibold ${
                              u.role === "it_admin"
                                ? "bg-purple-100 text-purple-800"
                                : u.role === "dept_admin"
                                  ? "bg-indigo-100 text-indigo-800"
                                  : "bg-slate-100 text-slate-700"
                            }`}
                          >
                            {formatRole(u.role)}
                          </span>
                        )}
                      </td>
                      <td className="py-3 px-4">
                        <span
                          className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full font-semibold ${
                            u.status === "active"
                              ? "bg-emerald-100 text-emerald-800"
                              : u.status === "pending_approval"
                                ? "bg-amber-100 text-amber-800"
                                : "bg-rose-100 text-rose-800"
                          }`}
                        >
                          {u.status === "active" ? (
                            <UserCheck className="w-3 h-3" />
                          ) : u.status === "pending_approval" ? (
                            <Clock className="w-3 h-3" />
                          ) : (
                            <UserX className="w-3 h-3" />
                          )}
                          {u.status === "active"
                            ? "Active"
                            : u.status === "pending_approval"
                              ? "Pending"
                              : "Suspended"}
                        </span>
                      </td>
                      <td className="py-3 px-4">
                        {isEditing && selectedRole === "dept_admin" ? (
                          <div className="flex flex-wrap gap-1 max-w-xs">
                            {AVAILABLE_APPS.map((app) => (
                              <button
                                key={app.id}
                                type="button"
                                onClick={() => toggleAppSelection(app.id)}
                                className={`px-1.5 py-0.5 rounded text-[10px] border transition-colors ${
                                  selectedApps.includes(app.id)
                                    ? "bg-teal-600 text-white border-teal-600"
                                    : "bg-slate-50 text-slate-600 border-slate-300"
                                }`}
                              >
                                {app.name}
                              </button>
                            ))}
                          </div>
                        ) : (
                          <div className="flex flex-wrap gap-1">
                            {u.role === "it_admin" ? (
                              <span className="text-slate-400 italic">
                                Global Access (All Apps)
                              </span>
                            ) : u.allowedApps && u.allowedApps.length > 0 ? (
                              u.allowedApps.map((appId) => (
                                <span
                                  key={appId}
                                  className="px-1.5 py-0.5 rounded bg-slate-100 text-slate-700 text-[10px]"
                                >
                                  {AVAILABLE_APPS.find((a) => a.id === appId)
                                    ?.name || appId}
                                </span>
                              ))
                            ) : (
                              <span className="text-slate-400 italic">
                                None
                              </span>
                            )}
                          </div>
                        )}
                      </td>
                      <td className="py-3 px-4">
                        {isEditing ? (
                          <select
                            value={selectedNotificationPref}
                            onChange={(e) =>
                              setSelectedNotificationPref(
                                e.target.value as NotificationPreference,
                              )
                            }
                            className="bg-white border border-slate-300 rounded px-2 py-1 text-xs"
                          >
                            <option value="realtime">Real-Time Alerts</option>
                            <option value="daily">
                              Daily Digest (8 AM CT)
                            </option>
                            <option value="twice_daily">
                              Twice Daily Digest
                            </option>
                            <option value="never">In-App Only (Never)</option>
                          </select>
                        ) : (
                          <button
                            type="button"
                            onClick={() => {
                              if (
                                currentUser.role === "it_admin" ||
                                currentUser.role === "dept_admin"
                              ) {
                                setEditingNotificationUser(u);
                              }
                            }}
                            disabled={
                              !(
                                currentUser.role === "it_admin" ||
                                currentUser.role === "dept_admin"
                              )
                            }
                            title={
                              currentUser.role === "it_admin" ||
                              currentUser.role === "dept_admin"
                                ? "Click to edit notification preference"
                                : undefined
                            }
                            className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full border text-[11px] font-medium transition ${getPrefBadgeClass(
                              u.notificationPreference,
                            )} ${
                              currentUser.role === "it_admin" ||
                              currentUser.role === "dept_admin"
                                ? "hover:opacity-80 cursor-pointer shadow-2xs"
                                : "cursor-default"
                            }`}
                          >
                            {renderPrefIcon(u.notificationPreference)}
                            <span>
                              {formatPrefLabel(u.notificationPreference)}
                            </span>
                            {(currentUser.role === "it_admin" ||
                              currentUser.role === "dept_admin") && (
                              <Pencil className="w-2.5 h-2.5 opacity-60 ml-0.5" />
                            )}
                          </button>
                        )}
                      </td>
                      <td className="py-3 px-4 text-right">
                        {isEditing ? (
                          <div className="inline-flex items-center gap-1.5">
                            <button
                              type="button"
                              onClick={() => void handleSaveGovernance(u)}
                              className="px-2.5 py-1 bg-teal-600 text-white rounded font-medium hover:bg-teal-700"
                            >
                              Save
                            </button>
                            <button
                              type="button"
                              onClick={() => setEditingUserId(null)}
                              className="px-2 py-1 text-slate-500 hover:text-slate-800"
                            >
                              Cancel
                            </button>
                          </div>
                        ) : u.status === "pending_approval" ? (
                          <div className="inline-flex items-center gap-2">
                            <button
                              type="button"
                              onClick={() => handleOpenApprovalModal(u)}
                              className="px-2.5 py-1 bg-amber-500 text-white rounded font-medium hover:bg-amber-600 shadow-xs inline-flex items-center gap-1"
                            >
                              <UserCheck className="w-3.5 h-3.5" />
                              <span>Approve...</span>
                            </button>
                            {currentUser.role === "it_admin" && (
                              <button
                                type="button"
                                onClick={() => void handleDeleteUser(u)}
                                className="p-1 text-slate-400 hover:text-rose-600 transition-colors"
                                title="Reject and remove user"
                              >
                                <Trash2 className="w-3.5 h-3.5" />
                              </button>
                            )}
                          </div>
                        ) : (
                          <div className="inline-flex items-center gap-1.5">
                            <button
                              type="button"
                              onClick={() => handleOpenEdit(u)}
                              className="px-2 py-1 text-teal-600 hover:text-teal-800 font-medium"
                            >
                              Edit
                            </button>
                            {(currentUser.role === "it_admin" ||
                              currentUser.role === "dept_admin") && (
                              <button
                                type="button"
                                onClick={() => setEditingNotificationUser(u)}
                                className="p-1 text-slate-400 hover:text-teal-600 transition-colors"
                                title="Edit notification preferences"
                                aria-label={`Edit notifications for ${u.displayName || u.email}`}
                              >
                                <Bell className="w-3.5 h-3.5" />
                              </button>
                            )}
                            {u.status === "active" ? (
                              <button
                                type="button"
                                onClick={() =>
                                  void handleSaveGovernance(u, "suspended")
                                }
                                className="px-2 py-1 text-rose-600 hover:text-rose-800"
                              >
                                Suspend
                              </button>
                            ) : (
                              <button
                                type="button"
                                onClick={() =>
                                  void handleSaveGovernance(u, "active")
                                }
                                className="px-2 py-1 text-emerald-600 hover:text-emerald-800"
                              >
                                Reactivate
                              </button>
                            )}
                            {currentUser.role === "it_admin" && (
                              <button
                                type="button"
                                onClick={() => void handleDeleteUser(u)}
                                className="p-1 text-slate-400 hover:text-rose-600 transition-colors ml-1"
                                title="Remove user"
                              >
                                <Trash2 className="w-3.5 h-3.5" />
                              </button>
                            )}
                          </div>
                        )}
                      </td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Modal: Add User from HR Directory (Operations Tool Pattern) */}
      {isHrModalOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-slate-900/60 backdrop-blur-xs animate-in fade-in duration-200">
          <div className="bg-white rounded-2xl shadow-2xl border border-slate-200 max-w-xl w-full flex flex-col max-h-[90vh] overflow-hidden">
            {/* Modal Header */}
            <div className="p-5 border-b border-slate-200 flex items-center justify-between">
              <div>
                <h3 className="text-base font-bold text-slate-900 flex items-center gap-2">
                  <UserPlus className="w-5 h-5 text-teal-600" />
                  Add User from HR Directory
                </h3>
                <p className="text-xs text-slate-500 mt-0.5">
                  Select staff from the HR directory to grant immediate access
                </p>
              </div>
              <button
                type="button"
                onClick={() => setIsHrModalOpen(false)}
                className="p-1 rounded-lg text-slate-400 hover:text-slate-700 hover:bg-slate-100 transition-colors"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            {/* Modal Content */}
            <div className="p-5 overflow-y-auto space-y-4 flex-1">
              {hrModalError && (
                <div className="p-3 bg-rose-50 border border-rose-200 text-rose-800 text-xs rounded-xl flex items-center gap-2">
                  <ShieldAlert className="w-4 h-4 text-rose-600 shrink-0" />
                  <span>{hrModalError}</span>
                </div>
              )}

              {/* Status Note */}
              <div className="p-2.5 bg-teal-50/60 border border-teal-200/80 rounded-xl text-xs text-teal-800 flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <Shield className="w-4 h-4 text-teal-600 shrink-0" />
                  <span>
                    Showing <strong>active staff only</strong>.
                  </span>
                </div>
                {!loadingHr && (
                  <span className="font-semibold text-[11px] bg-teal-100 px-2 py-0.5 rounded-full text-teal-800">
                    {hrStaff.length} active
                  </span>
                )}
              </div>

              {/* Search Staff Member */}
              <div className="space-y-1.5">
                <label className="text-xs font-semibold text-slate-700 block">
                  Search Staff Member
                </label>
                <div className="relative">
                  <Search className="w-4 h-4 text-slate-400 absolute left-3 top-1/2 -translate-y-1/2" />
                  <input
                    type="text"
                    value={hrSearchQuery}
                    onChange={(e) => setHrSearchQuery(e.target.value)}
                    placeholder="Filter by name, email or department..."
                    className="w-full pl-9 pr-3 py-2 text-xs bg-slate-50 border border-slate-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-teal-500"
                  />
                </div>
              </div>

              {/* Employee Selection List */}
              <div className="border border-slate-200 rounded-xl divide-y divide-slate-100 max-h-56 overflow-y-auto bg-slate-50/50">
                {loadingHr ? (
                  <div className="py-10 flex flex-col items-center justify-center gap-2 text-slate-500 text-xs">
                    <Loader2 className="w-6 h-6 animate-spin text-teal-600" />
                    <span>Loading active HR directory...</span>
                  </div>
                ) : filteredHrStaff.length === 0 ? (
                  <div className="py-8 text-center text-slate-500 text-xs">
                    No active staff matching &quot;{hrSearchQuery}&quot; found.
                  </div>
                ) : (
                  filteredHrStaff.map((staff) => {
                    const isSelected = selectedHrStaff?.id === staff.id;
                    const alreadyInApp = users.some(
                      (u) =>
                        u.email.toLowerCase() === staff.email.toLowerCase() ||
                        u.uid === staff.id,
                    );

                    return (
                      <div
                        key={staff.id}
                        onClick={() => setSelectedHrStaff(staff)}
                        className={`p-3 flex items-center justify-between cursor-pointer transition-colors ${
                          isSelected
                            ? "bg-teal-50/90 border-l-4 border-teal-600"
                            : "hover:bg-white"
                        }`}
                      >
                        <div className="flex items-center gap-3">
                          <div className="w-8 h-8 rounded-full bg-slate-200 text-slate-700 font-bold flex items-center justify-center text-xs">
                            {staff.displayName.slice(0, 2).toUpperCase()}
                          </div>
                          <div>
                            <div className="flex items-center gap-2">
                              <span className="text-xs font-semibold text-slate-900">
                                {staff.displayName}
                              </span>
                              {alreadyInApp && (
                                <span className="px-1.5 py-0.2 rounded-full bg-slate-200 text-slate-600 text-[10px] font-medium">
                                  Already in System
                                </span>
                              )}
                            </div>
                            <p className="text-[11px] text-slate-500">
                              {staff.email} • {staff.department} ({staff.title})
                            </p>
                          </div>
                        </div>

                        {isSelected && (
                          <Check className="w-4 h-4 text-teal-600 mr-2" />
                        )}
                      </div>
                    );
                  })
                )}
              </div>

              {/* Assign Access Tier Container (Operations Hub Pattern) */}
              {selectedHrStaff && (
                <div className="p-4 bg-slate-50 border border-slate-200 rounded-xl space-y-3 animate-in fade-in duration-150">
                  <div>
                    <label className="text-xs font-semibold text-slate-700 block">
                      Assign Access Tier for:{" "}
                      <span className="text-slate-900 font-bold">
                        {selectedHrStaff.displayName}
                      </span>
                    </label>
                    <select
                      value={selectedTier}
                      onChange={(e) =>
                        setSelectedTier(e.target.value as UserRole)
                      }
                      className="mt-1.5 w-full bg-white border border-slate-300 rounded-lg px-3 py-2 text-xs focus:ring-2 focus:ring-teal-500 font-medium"
                    >
                      <option value="end_user">
                        End User (Standard Tickets)
                      </option>
                      <option value="dept_admin">
                        Dept Admin (Department Lead / Scoped Apps)
                      </option>
                      <option value="it_admin">
                        IT Admin (Global Administration)
                      </option>
                    </select>
                  </div>

                  {selectedTier === "dept_admin" && (
                    <div className="space-y-1.5 pt-1">
                      <span className="text-[11px] font-medium text-slate-600 block">
                        Select Permitted Applications:
                      </span>
                      <div className="flex flex-wrap gap-1.5">
                        {AVAILABLE_APPS.map((app) => (
                          <button
                            key={app.id}
                            type="button"
                            onClick={() => toggleDeptAppSelection(app.id)}
                            className={`px-2 py-1 rounded text-xs border transition-colors ${
                              selectedDeptApps.includes(app.id)
                                ? "bg-teal-600 text-white border-teal-600"
                                : "bg-white text-slate-700 border-slate-300 hover:bg-slate-100"
                            }`}
                          >
                            {app.name}
                          </button>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              )}
            </div>

            {/* Modal Footer */}
            <div className="p-4 bg-slate-50 border-t border-slate-200 flex items-center justify-between">
              <span className="text-xs text-slate-500">
                {selectedHrStaff
                  ? `Selected: ${selectedHrStaff.displayName}`
                  : "Select a staff member"}
              </span>

              <div className="flex items-center gap-2">
                <button
                  type="button"
                  onClick={() => setIsHrModalOpen(false)}
                  disabled={isGrantingAccess}
                  className="px-3.5 py-2 text-xs font-semibold text-slate-700 hover:bg-slate-200 rounded-lg transition-colors"
                >
                  Cancel
                </button>

                <button
                  type="button"
                  onClick={() => void handleGrantAccessFromHr()}
                  disabled={!selectedHrStaff || isGrantingAccess}
                  className="btn-animate inline-flex items-center gap-2 px-4 py-2 bg-teal-600 text-white rounded-lg text-xs font-semibold shadow-sm hover:bg-teal-700 disabled:opacity-50 transition-colors"
                >
                  {isGrantingAccess ? (
                    <>
                      <Loader2 className="w-3.5 h-3.5 animate-spin" />
                      <span>Granting Access...</span>
                    </>
                  ) : (
                    <>
                      <UserCheck className="w-3.5 h-3.5" />
                      <span>Grant Access</span>
                    </>
                  )}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Modal: Approve User Access (For Pending Users) */}
      {approvingUser && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-slate-900/60 backdrop-blur-xs animate-in fade-in duration-200">
          <div className="bg-white rounded-2xl shadow-2xl border border-slate-200 max-w-md w-full flex flex-col overflow-hidden">
            {/* Modal Header */}
            <div className="p-5 border-b border-slate-200 flex items-center justify-between bg-amber-50/50">
              <div className="flex items-center gap-2.5">
                <div className="w-9 h-9 rounded-xl bg-amber-500 text-white flex items-center justify-center shadow-xs">
                  <UserCheck className="w-5 h-5" />
                </div>
                <div>
                  <h3 className="text-base font-bold text-slate-900">
                    Approve User Access
                  </h3>
                  <p className="text-xs text-slate-500">
                    Assign role and activate platform permissions
                  </p>
                </div>
              </div>
              <button
                type="button"
                onClick={() => setApprovingUser(null)}
                className="p-1 rounded-lg text-slate-400 hover:text-slate-700 hover:bg-slate-100 transition-colors"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            {/* Modal Body */}
            <div className="p-5 space-y-4">
              {/* User summary card */}
              <div className="p-3 bg-slate-50 border border-slate-200 rounded-xl flex items-center gap-3">
                {approvingUser.photoURL ? (
                  <img
                    src={approvingUser.photoURL}
                    alt={approvingUser.displayName || approvingUser.email}
                    className="w-10 h-10 rounded-full object-cover"
                  />
                ) : (
                  <div className="w-10 h-10 rounded-full bg-teal-600 text-white font-bold flex items-center justify-center text-sm">
                    {(approvingUser.displayName || approvingUser.email)
                      .slice(0, 2)
                      .toUpperCase()}
                  </div>
                )}
                <div>
                  <p className="font-semibold text-slate-900 text-xs">
                    {approvingUser.displayName || approvingUser.email}
                  </p>
                  <p className="text-[11px] text-slate-500">
                    {approvingUser.email}
                  </p>
                  {approvingUser.department && (
                    <p className="text-[10px] text-slate-600 mt-0.5 inline-flex items-center gap-1">
                      <Building className="w-3 h-3 text-slate-400" />
                      {approvingUser.department}
                    </p>
                  )}
                </div>
              </div>

              {/* Role Selection */}
              <div className="space-y-2">
                <label className="text-xs font-semibold text-slate-700 block">
                  Select Role & Access Level:
                </label>
                <select
                  value={approvalRole}
                  onChange={(e) => setApprovalRole(e.target.value as UserRole)}
                  className="w-full bg-white border border-slate-300 rounded-lg px-3 py-2 text-xs focus:ring-2 focus:ring-teal-500 font-medium"
                >
                  <option value="end_user">End User (Standard Tickets)</option>
                  <option value="dept_admin">
                    Dept Admin (Department Lead / Scoped Apps)
                  </option>
                  <option value="it_admin">
                    IT Admin (Global Administration)
                  </option>
                </select>
              </div>

              {approvalRole === "dept_admin" && (
                <div className="space-y-1.5 pt-1">
                  <span className="text-[11px] font-medium text-slate-600 block">
                    Permitted Applications:
                  </span>
                  <div className="flex flex-wrap gap-1.5">
                    {AVAILABLE_APPS.map((app) => (
                      <button
                        key={app.id}
                        type="button"
                        onClick={() => toggleApprovalApp(app.id)}
                        className={`px-2 py-1 rounded text-xs border transition-colors ${
                          approvalApps.includes(app.id)
                            ? "bg-teal-600 text-white border-teal-600"
                            : "bg-white text-slate-700 border-slate-300 hover:bg-slate-100"
                        }`}
                      >
                        {app.name}
                      </button>
                    ))}
                  </div>
                </div>
              )}
            </div>

            {/* Modal Footer */}
            <div className="p-4 bg-slate-50 border-t border-slate-200 flex items-center justify-between">
              <button
                type="button"
                onClick={() => void handleDeleteUser(approvingUser)}
                className="text-xs text-rose-600 hover:text-rose-800 font-medium inline-flex items-center gap-1"
              >
                <Trash2 className="w-3.5 h-3.5" />
                <span>Deny & Remove</span>
              </button>

              <div className="flex items-center gap-2">
                <button
                  type="button"
                  onClick={() => setApprovingUser(null)}
                  disabled={isApproving}
                  className="px-3 py-1.5 text-xs font-semibold text-slate-700 hover:bg-slate-200 rounded-lg transition-colors"
                >
                  Cancel
                </button>
                <button
                  type="button"
                  onClick={() => void handleExecuteApproval()}
                  disabled={isApproving}
                  className="btn-animate inline-flex items-center gap-1.5 px-4 py-1.5 bg-teal-600 text-white rounded-lg text-xs font-semibold shadow-sm hover:bg-teal-700 disabled:opacity-50 transition-colors"
                >
                  {isApproving ? (
                    <>
                      <Loader2 className="w-3.5 h-3.5 animate-spin" />
                      <span>Approving...</span>
                    </>
                  ) : (
                    <>
                      <UserCheck className="w-3.5 h-3.5" />
                      <span>Approve & Grant Access</span>
                    </>
                  )}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Modal: Edit User Notification Preferences (Admin Access) */}
      {editingNotificationUser && (
        <NotificationPreferencesModal
          isOpen={!!editingNotificationUser}
          onClose={() => setEditingNotificationUser(null)}
          currentUser={currentUser}
          targetUser={editingNotificationUser}
          onSaved={(newPref) => {
            setUsers((prev) =>
              prev.map((u) =>
                u.uid === editingNotificationUser.uid
                  ? { ...u, notificationPreference: newPref }
                  : u,
              ),
            );
            setSyncFeedback(
              `Updated notification preferences for ${
                editingNotificationUser.displayName ||
                editingNotificationUser.email
              } to ${formatPrefLabel(newPref)}.`,
            );
          }}
        />
      )}
    </div>
  );
};
